import { type CMSProfile } from '@/app/routes/Admin/Users/users/interfaces';
import { type Paginated } from '@/types';
import type {
  PaginatedCMSChargePointList,
  PaginatedCMSChargingSessionList,
  PaginatedCMSPaymentList,
} from '@/types/cms';
import type {
  CollectionNoteSummary,
  ExportOutput,
  PaginatedCMSAssetList,
  PaginatedCMSConnectorList,
  PaginatedCMSContractList,
  PaginatedCMSContractSnapshotList,
  PaginatedCMSOcppChargePointList,
  PaginatedCMSSampledValueList,
  PaginatedCMSTransactionList,
  PaginatedCMSTransactionRequestList,
  PaginatedCMSUserGroupList,
} from '@/types/swagger';
import { http } from 'config';
import * as z from 'zod';

import {
  ACCOUNT_CMS_USERS_URL,
  CMS_ASSETS_URL,
  CMS_CHARGEPOINTS_OCPP_URL,
  CMS_CHARGEPOINTS_URL,
  CMS_CONNECTORS_URL,
  CMS_CONTRACT_SNAPSHOTS_URL,
  CMS_CONTRACTS_URL,
  CMS_HISTORY_CHARGING_SESSIONS_SALES_DOWNLOAD_XLSX_URL,
  CMS_HISTORY_CHARGING_SESSIONS_URL,
  CMS_HISTORY_PAYMENTS_SALES_DOWNLOAD_XLSX_URL,
  CMS_HISTORY_PAYMENTS_URL,
  CMS_SAMPLED_VALUES_URL,
  CMS_TRANSACTION_REQUESTS_URL,
  CMS_TRANSACTIONS_REPORT_URL,
  CMS_TRANSACTIONS_URL,
  CMS_USER_GROUPS_URL,
  CMS_USERS_URL,
  COLLECTION_NOTES_SUMMARY_URL,
  COMPANY_CMS_USER_GROUPS_URL,
  SAMPLED_VALUES_BY_TRANSACTION_URL,
} from './endpoints';

const numberArraySchema = z
  .array(z.coerce.number().int().positive())
  .transform((arr) => arr.join(','));

const stringArraySchema = z.array(z.string()).transform((arr) => arr.join(','));

const utcDateTimeSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/,
    'Must be a UTC datetime string in format YYYY-MM-DDTHH:mm:ssZ'
  )
  .refine((dateStr) => {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  }, 'Must be a valid UTC datetime');

const orderByEnum = z.enum([
  'payment_status',
  '-payment_status',
  'charging_authorization_start',
  '-charging_authorization_start',
  'charging_authorization_stop',
  '-charging_authorization_stop',
  'connector',
  '-connector',
  'user_start',
  '-user_start',
  'user_stop',
  '-user_stop',
  'vehicle',
  '-vehicle',
  'timestamp_start',
  '-timestamp_start',
  'timestamp_stop',
  '-timestamp_stop',
  'created',
  '-created',
  'updated',
  '-updated',
  'gross_price',
  '-gross_price',
  'net_price',
  '-net_price',
  'odometer',
  '-odometer',
  'soc_start',
  '-soc_start',
  'soc_stop',
  '-soc_stop',
  'max_power',
  '-max_power',
  'avg_power',
  '-avg_power',
  'total_energy',
  '-total_energy',
  'soh',
  '-soh',
]);

const paymentStatusEnum = z.enum([
  'Error',
  'Failed',
  'Min Price',
  'Partial',
  'Pending',
  'Processing',
  'Success',
  'Void',
  'Waiting',
]);

// ! This doesn't have type safety for the params, this needs a proper interface and then we can use it to build or params to pass to the API
// ! The return type also needs to be a zod schema (or a valibot schema, whatever you prefer) to ensure type safety on runtime and not just on compile time
export const getCmsAssets = async (params?: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSAssetList>(CMS_ASSETS_URL, {
    params,
  });
  return data;
};

const paymentOrderByEnum = z.enum([
  'gross_amount',
  '-gross_amount',
  'net_amount',
  '-net_amount',
  'created',
  '-created',
  'user_group_name',
  '-user_group_name',
]);

const paymentOrderBySchema = z.array(paymentOrderByEnum).transform((arr) => arr.join(','));

const getPaymentsSchema = z.object({
  authenticated_payment: z.coerce.boolean().optional(),
  charging_session_id__in: stringArraySchema.optional(),
  created__gte: utcDateTimeSchema.optional(),
  created__lte: utcDateTimeSchema.optional(),
  debit_card: z.coerce.boolean().optional(),
  gross_amount__gte: z.coerce.number().min(0).optional(),
  gross_amount__lte: z.coerce.number().min(0).optional(),
  kushki_ticket_number: z.string().optional(),
  net_amount__gte: z.coerce.number().min(0).optional(),
  net_amount__lte: z.coerce.number().min(0).optional(),
  order: paymentOrderBySchema.optional(),
  page: z.coerce.number().min(1).default(1),
  page_size: z.coerce.number().min(50).default(50),
  user_group_id__in: stringArraySchema.optional(),
});

export type GetPaymentParams = z.input<typeof getPaymentsSchema>;

export const getCmsPayments = async (params: GetPaymentParams) => {
  const validatedParams = getPaymentsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data } = await http.get<PaginatedCMSPaymentList>(CMS_HISTORY_PAYMENTS_URL, {
    params: validatedParams.data,
  });
  return data;
};

export const getCmsContractSnapshots = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSContractSnapshotList>(CMS_CONTRACT_SNAPSHOTS_URL, {
    params,
  });
  return data;
};

export const getCmsContracts = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSContractList>(CMS_CONTRACTS_URL, {
    params,
  });
  return data;
};

const orderBySchema = z.array(orderByEnum).transform((arr) => arr.join(','));

const paymentStatusSchema = z.array(paymentStatusEnum).transform((arr) => arr.join(','));

const getTransactionsSchema = z.object({
  asset__id__in: numberArraySchema.optional(),
  avg_power__gte: z.coerce.number().min(0).optional(),
  avg_power__lte: z.coerce.number().min(0).optional(),
  charging_authorization_start__in: numberArraySchema.optional(),
  charging_authorization_stop__in: numberArraySchema.optional(),
  connector__charge_point__verbose_name__contains: z.string().optional(),
  connector__in: numberArraySchema.optional(),
  created__gte: utcDateTimeSchema.optional(),
  created__lte: utcDateTimeSchema.optional(),
  gross_price__gte: z.coerce.number().min(0).optional(),
  gross_price__lte: z.coerce.number().min(0).optional(),
  id__in: numberArraySchema.optional(),
  max_power__gte: z.coerce.number().min(0).optional(),
  max_power__lte: z.coerce.number().min(0).optional(),
  net_price__gte: z.coerce.number().min(0).optional(),
  net_price__lte: z.coerce.number().min(0).optional(),
  odometer__gte: z.coerce.number().min(0).optional(),
  odometer__lte: z.coerce.number().min(0).optional(),
  order: orderBySchema.optional(),
  page: z.coerce.number().min(1).default(1).optional(),
  page_size: z.coerce.number().min(1).default(50).optional(),
  payment_status__in: paymentStatusSchema.optional(),
  soc_start__gte: z.coerce.number().min(0).optional(),
  soc_start__lte: z.coerce.number().min(0).optional(),
  soc_stop__gte: z.coerce.number().min(0).optional(),
  soc_stop__lte: z.coerce.number().min(0).optional(),
  soh__gte: z.coerce.number().min(0).optional(),
  soh__lte: z.coerce.number().min(0).optional(),
  timestamp_start__gte: utcDateTimeSchema.optional(),
  timestamp_start__lte: utcDateTimeSchema.optional(),
  timestamp_stop__gte: utcDateTimeSchema.optional(),
  timestamp_stop__lte: utcDateTimeSchema.optional(),
  total_energy__gte: z.coerce.number().min(0).optional(),
  total_energy__lte: z.coerce.number().min(0).optional(),
  updated_gte: utcDateTimeSchema.optional(),
  updated_lte: utcDateTimeSchema.optional(),
  user_group_id__in: numberArraySchema.optional(),
  user_start__email__contains: z.string().optional(),
  user_start__exists: z.coerce.boolean().optional(),
  user_start__in: numberArraySchema.optional(),
  user_stop__in: numberArraySchema.optional(),
  vehicle__in: stringArraySchema.optional(),
});

export type GetCmsTransactionsParams = z.input<typeof getTransactionsSchema>;

export const getCmsTransactions = async (params: GetCmsTransactionsParams) => {
  const validatedParams = getTransactionsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data } = await http.get<PaginatedCMSTransactionList>(CMS_TRANSACTIONS_URL, {
    params: validatedParams.data,
  });
  return data;
};

export const chargingSessionOrderByEnum = z.enum([
  'timestamp_start',
  '-timestamp_start',
  'timestamp_stop',
  '-timestamp_stop',
  'net_price',
  '-net_price',
  'charging_authorization_start__vehicle__verbose_name',
  '-charging_authorization_start__vehicle__verbose_name',
]);

const chargingSessionOrderBySchema = z
  .array(chargingSessionOrderByEnum)
  .transform((arr) => arr.join(','));

const getCmsChargingSessionsSchema = z.object({
  charging_authorization_start__vehicle__verbose_name__icontains: z.string().optional(),
  connector__chargepoint__verbose_name__icontains: z.string().optional(),
  gross_price__gte: z.coerce.number().min(0).optional(),
  gross_price__lte: z.coerce.number().min(0).optional(),
  has_contract_snapshot: z.boolean().optional(),
  id__in: stringArraySchema.optional(),
  is_anonymous_user: z.coerce.boolean().optional(),
  net_price__gte: z.coerce.number().min(0).optional(),
  net_price__lte: z.coerce.number().min(0).optional(),
  order: chargingSessionOrderBySchema.optional(),
  page: z.coerce.number().min(1).default(1).optional(),
  page_size: z.coerce.number().min(1).default(50).optional(),
  payment_status__in: paymentStatusSchema.optional(),
  timestamp_start__gte: utcDateTimeSchema.optional(),
  timestamp_start__lte: utcDateTimeSchema.optional(),
  timestamp_stop__gte: utcDateTimeSchema.optional(),
  timestamp_stop__lte: utcDateTimeSchema.optional(),
  user_email__icontains: z.string().optional(),
  user_group_id__in: numberArraySchema.optional(),
});

export type GetCmsChargingSessionsParams = z.input<typeof getCmsChargingSessionsSchema>;

export const getCmsChargingSessions = async (params: GetCmsChargingSessionsParams) => {
  const validatedParams = getCmsChargingSessionsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data } = await http.get<PaginatedCMSChargingSessionList>(
    CMS_HISTORY_CHARGING_SESSIONS_URL,
    {
      params: validatedParams.data,
    }
  );
  return data;
};

export const getCmsTransactionRequests = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSTransactionRequestList>(
    CMS_TRANSACTION_REQUESTS_URL,
    { params }
  );
  return data;
};

const orderEnum = z.enum(['-date_joined', '-email', '-id', 'date_joined', 'email', 'id']);

export const getFormattedOrder = (sort: string, order: string) => {
  const formattedOrder = order === 'desc' ? '-' : '';
  const result = orderEnum.safeParse(`${formattedOrder}${sort}`);
  return result.success ? result.data : undefined;
};

const userQueryParamsSchema = z.object({
  RFID: z.string().optional(),
  document_type__in: z
    .array(z.enum(['', 'CC', 'CE', 'DNI', 'NIT', 'null', 'Passport', 'RFC', 'RUT']))
    .transform((arr) => arr.join(','))
    .optional(),
  email__contains: z.string().optional(),
  group__in: z
    .number()
    .array()
    .transform((arr) => arr.join(','))
    .optional(),
  id__in: z
    .number()
    .array()
    .transform((arr) => arr.join(','))
    .optional(),
  is_active: z.boolean().optional(),
  is_mobile: z.boolean().optional(),
  order: z
    .array(orderEnum)
    .transform((arr) => arr.join(','))
    .optional(),
  page: z.coerce.number().default(1).optional(),
  page_size: z.coerce.number().default(50).optional(),
});

export const getCmsUsers = async (params: z.input<typeof userQueryParamsSchema>) => {
  const validatedParams = userQueryParamsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;
  const { data } = await http.get<Paginated<CMSProfile>>(ACCOUNT_CMS_USERS_URL, {
    params: validatedParams.data,
  });
  return data;
};

// ! This endpoint is basically duplicated but we are using both for some reason
export const getUsers = async (params: URLSearchParams) => {
  const { data } = await http.get<Paginated<CMSProfile>>(CMS_USERS_URL, {
    params,
  });
  return data;
};

export const getCmsConnectors = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSConnectorList>(CMS_CONNECTORS_URL, {
    params,
  });
  return data;
};

export const getCmsSampledValues = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSSampledValueList>(CMS_SAMPLED_VALUES_URL, {
    params,
  });
  return data;
};

export const getCmsOcppChargePoints = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSOcppChargePointList>(CMS_CHARGEPOINTS_OCPP_URL, {
    params,
  });
  return data;
};

export const getCmsChargePoints = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSChargePointList>(CMS_CHARGEPOINTS_URL, {
    params,
  });
  return data;
};

export const getCmsCollectionNotesSummary = async (params: URLSearchParams) => {
  const { data } = await http.get<CollectionNoteSummary>(COLLECTION_NOTES_SUMMARY_URL, { params });
  return data;
};

// ! This endpoint is basically duplicated but we are using both for some reason
export const getCmsUserGroups = async (pageSize = 999) => {
  const { data } = await http.get<PaginatedCMSUserGroupList>(COMPANY_CMS_USER_GROUPS_URL, {
    params: { page_size: pageSize },
  });
  return data;
};

// ! This doesn't have type safety for the params, this needs a proper interface and then we can use it to build or params to pass to the API
// ! The return type also needs to be a zod schema (or a valibot schema, whatever you prefer) to ensure type safety on runtime and not just on compile time
export const getUserGroups = async (params: URLSearchParams) => {
  const { data } = await http.get<PaginatedCMSUserGroupList>(CMS_USER_GROUPS_URL, { params });
  return data;
};

const downloadCmsPaymentsSchema = getPaymentsSchema.extend({
  created__gte: utcDateTimeSchema,
  created__lte: utcDateTimeSchema,
});

export type DownloadCmsPaymentsParams = z.input<typeof downloadCmsPaymentsSchema>;

export const requestDownloadCmsPayments = async (params: DownloadCmsPaymentsParams) => {
  const validatedParams = downloadCmsPaymentsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data, status } = await http.get<ExportOutput>(
    CMS_HISTORY_PAYMENTS_SALES_DOWNLOAD_XLSX_URL,
    {
      params: validatedParams.data,
    }
  );
  return { data, status };
};

export const downloadCmsChargesSchema = getCmsChargingSessionsSchema.extend({
  has_contract_snapshot: z.coerce.boolean(),
  timestamp_stop__gte: utcDateTimeSchema,
  timestamp_stop__lte: utcDateTimeSchema,
});

export type DownloadCmsChargesParams = z.input<typeof downloadCmsChargesSchema>;

export const requestDownloadCmsCharges = async (params: DownloadCmsChargesParams) => {
  const validatedParams = downloadCmsChargesSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data, status } = await http.get<ExportOutput>(
    CMS_HISTORY_CHARGING_SESSIONS_SALES_DOWNLOAD_XLSX_URL,
    { params: validatedParams.data }
  );
  return { data, status };
};

export const downloadCmsTransactionsSchema = getTransactionsSchema.extend({
  created__gte: utcDateTimeSchema,
  created__lte: utcDateTimeSchema,
});

export type DownloadCmsTransactionsParams = z.input<typeof downloadCmsTransactionsSchema>;

export const requestDownloadCmsTransactions = async (params: DownloadCmsTransactionsParams) => {
  const validatedParams = downloadCmsTransactionsSchema.safeParse(params);
  if (!validatedParams.success) throw validatedParams.error;

  const { data, status } = await http.get<ExportOutput>(CMS_TRANSACTIONS_REPORT_URL, {
    params: validatedParams.data,
  });
  return { data, status };
};

// ! Reminder to ask why this endpoint may need page and page size when it only downloads one transaction
export const requestDownloadOneTransaction = async (
  transactionId: number,
  page = 1,
  pageSize = 999,
  sendS3 = true
) =>
  await http.get<ExportOutput>(SAMPLED_VALUES_BY_TRANSACTION_URL(transactionId), {
    params: { page, page_size: pageSize, send_s3: sendS3 },
  });
